/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'

import {
  Heading,
} from '../'

import { ActionButton } from './ActionButton'
import { WorkflowInitiateButton } from './WorkflowInitiateButton'


export class RequestProcessingSidebar extends React.PureComponent {
  static propTypes = {
    onReclassifyClick: PropTypes.func,
    onReallocateClick: PropTypes.func,
    onRecaptureClick: PropTypes.func,
    onInValidateClick: PropTypes.func,
    onValidateClick: PropTypes.func,
    onInitiateClick: PropTypes.func,
    onHoldClick: PropTypes.func,
    onReleaseClick: PropTypes.func,
    onCancelClick: PropTypes.func,
    onFixCapturingClick: PropTypes.func,
    onRecheckErrorClick: PropTypes.func,
    onIgnoreErrorClick: PropTypes.func,
    onMarkCurrentStepApprovedClick: PropTypes.func,
    onViewOriginalFileClick: PropTypes.func,
    initiateInProgress: PropTypes.bool,
    userIsContentCaptor: PropTypes.bool,
    isCaptured: PropTypes.bool,
    isValidated: PropTypes.bool,
    processingRequired: PropTypes.bool,
    isProcessing: PropTypes.bool,
    sources: PropTypes.array,
    status: PropTypes.string,
    userMayValidate: PropTypes.bool,
    userMayInvalidate: PropTypes.bool
  }

  render() {
    const { onReclassifyClick, onReallocateClick, onRecaptureClick, onInvalidateClick,
      onValidateClick, onInitiateClick, onHoldClick, onReleaseClick, onCancelClick, onFixCapturingClick,
      onRecheckErrorClick, onIgnoreErrorClick, onMarkCurrentStepApprovedClick, onViewOriginalFileClick,
      reclassifyInProgress, initiateInProgress, userIsContentCaptor, isCaptured, isValidated,
      processingRequired, isProcessing, sources, status, userMayValidate, userMayInvalidate, requesttype,
      version, identity } = this.props

    const onHold = (status === 'ON_HOLD')
    const showViewOriginalButton = (status === 'PROCESSED' || isValidated)

    return (
      <div className='slds-size_1-of-6 slds-text-color_inverse slds-scrollable_y' style={{ backgroundColor: '#6c818f', padding: '1.5rem 0.25rem 1.5rem 1.25rem' }}>
        <Heading className='side-panel__title u-text-caps slds-m-bottom_xx-large'>
          Actions
        </Heading>

        <ActionButton
          disabled={onReclassifyClick === null}
          onClick={onReclassifyClick}>
          {reclassifyInProgress
            ? 'Reclassifying...'
            : 'Re-classify'
          }
        </ActionButton>

        <ActionButton
          disabled={onReallocateClick === null}
          onClick={onReallocateClick}>
          Re-allocate
        </ActionButton>

        <ActionButton
          disabled={onRecaptureClick === null}
          onClick={onRecaptureClick}>
          Re-capture
        </ActionButton>

        <ActionButton
          iconSymbol='dislike'
          disabled={!userMayInvalidate}
          onClick={onInvalidateClick}
          titleOnHover={
            userIsContentCaptor
              ? 'User captured content, someone else needs to invalidate'
              : isCaptured
                ? isValidated
                  ? 'Capture has already been validated'
                  : 'Invalidate.  Confirm incorrect capture.'
                : 'Capture needs completion before validation possible'
          }>
          Invalidate
        </ActionButton>
        <ActionButton
          iconSymbol='approval'
          disabled={!userMayValidate}
          onClick={onValidateClick}
          titleOnHover={
            userIsContentCaptor
              ? 'User captured content, someone else needs to validate'
              : isCaptured
                ? isValidated
                  ? 'Capture has already been validated'
                  : 'Validate that details have been captured correctly'
                : 'Capture needs completion before validation possible'
          }>
          Validate
        </ActionButton>

        {processingRequired && isProcessing === false &&
          <WorkflowInitiateButton
            requestRun={{
              requesttype,
              version,
              identity
            }}
            disabled={onInitiateClick === null}
            onInitiateClick={onInitiateClick}
            initiateInProgress={initiateInProgress}
          />
        }

        <div className='slds-p-top_small'>
          <ActionButton
            iconSymbol={onHold
              ? 'unlock'
              : 'lock'
            }
            onClick={onHold
              ? onReleaseClick
              : onHoldClick
            }
            disabled={
              (onHold && onReleaseClick === null) ||
              (!onHold && onHoldClick === null)
            }>
            {onHold
              ? 'Remove hold'
              : 'Place on hold'
            }
          </ActionButton>
          <ActionButton
            iconSymbol='close'
            disabled={onCancelClick === null}
            onClick={onCancelClick}>
            Cancel
          </ActionButton>
        </div>

        <div className='slds-p-top_small'>
          <ActionButton
            iconSymbol='custom_apps'
            disabled={onFixCapturingClick === null}
            onClick={onFixCapturingClick}
            titleOnHover='Allows you to fix data if it got stuck on step 2'>
            Fix capturing mistake
          </ActionButton>
          <ActionButton
            iconSymbol='cases'
            disabled={onRecheckErrorClick === null}
            onClick={onRecheckErrorClick}
            titleOnHover='Resets the workflow to check if data issue has been solved'>
            Recheck error
          </ActionButton>
          <ActionButton
            iconSymbol='magicwand'
            disabled={onIgnoreErrorClick === null}
            onClick={onIgnoreErrorClick}
            titleOnHover='Ignores error reported in workflow step 2'>
            Ignore error
          </ActionButton>
          <ActionButton
            iconSymbol='mark_all_as_read'
            disabled={onMarkCurrentStepApprovedClick === null}
            onClick={onMarkCurrentStepApprovedClick}
            titleOnHover='Approves current manual step (sets status to processed)'>
            Mark current step approved
          </ActionButton>
        </div>

        {showViewOriginalButton &&
          <ActionButton
            iconSymbol='preview'
            disabled={sources.length === 0}
            onClick={onViewOriginalFileClick}
            titleOnHover={sources.length === 0
              ? 'No files associated with request'
              : null
            }
            className='slds-p-top_small'>
            View original file
          </ActionButton>
        }
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/request-processing-sidebar/index.js